import {
  Canvas,
  Circle,
  FabricImage,
  Point,
  Rect,
  Textbox,
  Triangle,
  Polyline,
} from 'fabric';

import {
  BASE_TEXT_STYLE,
  OBJECT_SOURCE_KEY,
  DEFAULT_SHAPE_SIZE,
  TEXT_PLACEHOLDER,
  OBJECT_DATA_SHAPE_KEY,
  DRAW_OBJECT_TYPE_SQUARE,
  OBJECT_ARTBOARD_KEY,
  DRAW_OBJECT_TYPE_RECT,
  DRAW_OBJECT_TYPE_LINE,
} from '../draw-canvas.constants';
import {
  addTextListeners,
  customiseControlPoints,
  customiseControlStyles,
  customiseLineControlPoints,
  customiseRectControlPoints,
  customiseShapeControlPoints,
  customiseSquareControlPoints,
  initialiseDeleteControl,
} from './draw-controls';
import { grey1, grey10, grey5 } from 'approot/shared/styles/colours.style';
import { FONT_CALIBRE_SCHOOL } from 'approot/shared/styles/typography.style';

export async function addText({
  canvas,
  point,
  objectSource,
}: {
  canvas: Canvas;
  point: Point;
  objectSource?: string;
}) {
  const text = new Textbox(TEXT_PLACEHOLDER, {
    ...BASE_TEXT_STYLE,
    fontSize: 24,
    fontFamily: FONT_CALIBRE_SCHOOL,
    left: point.x,
    top: point.y,
    selected: true,
    selectable: true,
    editable: true,
    width: 140,
    fill: '#aaa',
    [OBJECT_SOURCE_KEY]: objectSource || undefined,
  });

  customiseControlPoints(text);

  canvas.add(text);
  canvas.setActiveObject(text);
  canvas.requestRenderAll();

  addTextListeners(canvas, text);

  text.enterEditing();

  await initialiseDeleteControl(canvas, text);

  return text;
}

export async function addCircle({
  canvas,
  point,
  strokeColour,
  fillColour,
  radius,
}: {
  canvas: Canvas;
  point: Point;
  strokeColour?: string;
  fillColour?: string;
  radius?: number;
}) {
  const circle = new Circle({
    left: point.x,
    top: point.y,
    selected: true,
    selectable: true,
    editable: true,
    originX: 'left',
    originY: 'top',
    radius: radius || DEFAULT_SHAPE_SIZE / 2,
    fill: fillColour || grey5,
    stroke: strokeColour || grey10,
    strokeWidth: 1,
    moveCursor: 'none',
    hoverCursor: 'none',
    noScaleCache: false,
    strokeUniform: true,
  });

  await initialiseDeleteControl(canvas, circle);
  customiseControlStyles(circle);
  customiseShapeControlPoints(circle);

  canvas.add(circle);
  canvas.requestRenderAll();

  return circle;
}

export async function addTriangle({
  canvas,
  point,
  strokeColour,
  fillColour,
  width,
  height,
}: {
  canvas: Canvas;
  point: Point;
  strokeColour?: string;
  fillColour?: string;
  width?: number;
  height?: number;
}) {
  const triangle = new Triangle({
    left: point.x,
    top: point.y,
    selected: true,
    selectable: true,
    editable: true,
    originX: 'left',
    originY: 'top',
    width: width || DEFAULT_SHAPE_SIZE,
    height: height || DEFAULT_SHAPE_SIZE,
    fill: fillColour || grey5,
    stroke: strokeColour || grey10,
    strokeWidth: 1,
    moveCursor: 'none',
    hoverCursor: 'none',
    noScaleCache: false,
    strokeUniform: true,
  });

  await initialiseDeleteControl(canvas, triangle);
  customiseControlStyles(triangle);
  customiseShapeControlPoints(triangle);

  canvas.add(triangle);
  canvas.requestRenderAll();

  return triangle;
}

export async function addRectangle({
  canvas,
  point,
  strokeColour,
  fillColour,
  width,
  height,
}: {
  canvas: Canvas;
  point: Point;
  strokeColour?: string;
  fillColour?: string;
  width?: number;
  height?: number;
}) {
  const rect = new Rect({
    left: point.x,
    top: point.y,
    selected: true,
    selectable: true,
    editable: true,
    originX: 'left',
    originY: 'top',
    width: width || DEFAULT_SHAPE_SIZE,
    height: height || DEFAULT_SHAPE_SIZE,
    fill: fillColour || grey5,
    stroke: strokeColour || grey10,
    strokeWidth: 1,
    moveCursor: 'none',
    hoverCursor: 'none',
    noScaleCache: false,
    strokeUniform: true,
    [OBJECT_DATA_SHAPE_KEY]: DRAW_OBJECT_TYPE_RECT,
  });

  await initialiseDeleteControl(canvas, rect);
  customiseControlStyles(rect);
  customiseRectControlPoints(rect);

  canvas.add(rect);
  canvas.requestRenderAll();

  return rect;
}

export async function addSquare({
  canvas,
  point,
  strokeColour,
  fillColour,
  size,
}: {
  canvas: Canvas;
  point: Point;
  strokeColour?: string;
  fillColour?: string;
  size?: number;
}) {
  const side = size || DEFAULT_SHAPE_SIZE;
  const rect = new Rect({
    left: point.x,
    top: point.y,
    selected: true,
    selectable: true,
    editable: true,
    originX: 'left',
    originY: 'top',
    width: side,
    height: side,
    fill: fillColour || grey5,
    stroke: strokeColour || grey10,
    strokeWidth: 1,
    moveCursor: 'none',
    hoverCursor: 'none',
    noScaleCache: false,
    strokeUniform: true,
    [OBJECT_DATA_SHAPE_KEY]: DRAW_OBJECT_TYPE_SQUARE,
    lockUniScaling: true,
  });

  await initialiseDeleteControl(canvas, rect);
  customiseControlStyles(rect);
  customiseSquareControlPoints(rect);

  canvas.add(rect);
  canvas.requestRenderAll();

  return rect;
}

export function addArtboard(
  canvas: Canvas,
  dimensions: { width: number; height: number }
) {
  const artboard = new Rect({
    left: 0,
    top: 0,
    originX: 'left',
    originY: 'top',
    width: dimensions.width,
    height: dimensions.height,
    stroke: grey5,
    strokeWidth: 1,
    fill: grey1,
    selectable: false,
    hoverCursor: 'default',
    [OBJECT_ARTBOARD_KEY]: true,
  });

  canvas.add(artboard);

  return artboard;
}

export async function addLine({
  canvas,
  point,
  strokeColour,
  x1,
  y1,
  x2,
  y2,
}: {
  canvas: Canvas;
  point: Point;
  strokeColour?: string;
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
}) {
  // If coordinates are provided (drag), use them; otherwise center line at point
  const lineX1 = x1 !== undefined ? x1 : point.x - DEFAULT_SHAPE_SIZE / 2;
  const lineY1 = y1 !== undefined ? y1 : point.y;
  const lineX2 = x2 !== undefined ? x2 : point.x + DEFAULT_SHAPE_SIZE / 2;
  const lineY2 = y2 !== undefined ? y2 : point.y;

  const line = new Polyline(
    [
      { x: lineX1, y: lineY1 },
      { x: lineX2, y: lineY2 },
    ],
    {
      selected: true,
      selectable: true,
      editable: true,
      originX: 'left',
      originY: 'top',
      stroke: strokeColour || grey10,
      strokeWidth: 1,
      fill: '',
      moveCursor: 'none',
      hoverCursor: 'none',
      noScaleCache: false,
      strokeUniform: false,
      hasBorders: true,
      [OBJECT_DATA_SHAPE_KEY]: DRAW_OBJECT_TYPE_LINE,
    }
  );

  customiseLineControlPoints(line);
  await initialiseDeleteControl(canvas, line);
  customiseControlStyles(line);

  canvas.add(line);
  canvas.requestRenderAll();

  return line;
}

export function addBackgroundImage(
  canvas: Canvas,
  imageUrl: string,
  dimensions: { width: number; height: number },
  onComplete: (img: FabricImage) => void
) {
  FabricImage.fromURL(imageUrl, { crossOrigin: 'anonymous' }).then(img => {
    const imagePadding = 4;
    if (img.width && img.height) {
      const hRatio = (dimensions.width - imagePadding) / img.width;
      const vRatio = (dimensions.height - imagePadding) / img.height;
      const ratio = Math.min(hRatio, vRatio);

      img.scaleToWidth(img.width * ratio);
      img.scaleToHeight(img.height * ratio);
      img.originX = 'left';
      img.originY = 'top';
      img.left = (dimensions.width - img.width * ratio) / 2;
      img.top = (dimensions.height - img.height * ratio) / 2;
      img.lockMovementY = true;
      img.lockMovementX = true;
      img.hoverCursor = 'default';
      img.selectable = false;

      canvas.add(img);
      onComplete(img);
    }
  });
}
